import React from 'react'

/* ── Generic icon (used for tech chips) ── */
const TechIcon = ({ label, color }) => (
  <div className="cpo-tech-icon" style={{ background: `linear-gradient(135deg, ${color}, ${color}CC)` }}>
    {label}
  </div>
)

/* ── Mode icons ── */
const ModeIcon = ({ path, color }) => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {path}
  </svg>
)

const Check = ({ color = '#16A34A' }) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
)

/* ════════════════════════════════════════════════════════════
   DEFAULT DATA — Java Full Stack (course_id = 1)
   ════════════════════════════════════════════════════════════ */
export const JAVA_FULLSTACK_OVERVIEW = {
  technologies: [
    { name: 'Java',          label: 'J',  color: '#F89820' },
    { name: 'Spring Boot',   label: 'SB', color: '#6DB33F' },
    { name: 'Hibernate',     label: 'H',  color: '#BCA000' },
    { name: 'MySQL',         label: 'My', color: '#00758F' },
    { name: 'HTML5',         label: 'H5', color: '#E34F26' },
    { name: 'CSS3',          label: 'C3', color: '#2965F1' },
    { name: 'JavaScript ES6',label: 'JS', color: '#F7DF1E' },
    { name: 'Bootstrap',     label: 'B',  color: '#7952B3' },
    { name: 'Git & GitHub',  label: 'Git',color: '#181717' },
    { name: 'Postman',       label: 'P',  color: '#FF6C37' },
    { name: 'Maven',         label: 'M',  color: '#C71A36' },
  ],

  modes: [
    {
      title: 'Offline',
      desc: 'Classroom-based training with hands-on lab sessions and direct mentor interaction.',
      color: '#2563EB',
      icon: <path d="M3 21h18M5 21V7l8-4 8 4v14M9 21v-6h6v6" />,
    },
    {
      title: 'Online',
      desc: 'Live interactive sessions with remote access to projects, recordings, and mentor support.',
      color: '#16A34A',
      icon: <><rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" /></>,
    },
    {
      title: 'Hybrid',
      desc: 'Flexible blend of classroom and online learning to match your schedule and pace.',
      color: '#7C3AED',
      icon: <><path d="M17 1l4 4-4 4" /><path d="M3 11V9a4 4 0 014-4h14" /><path d="M7 23l-4-4 4-4" /><path d="M21 13v2a4 4 0 01-4 4H3" /></>,
    },
  ],

  internships: [
    {
      duration: '3 Months Internship',
      color: '#16A34A',
      bg: '#F0FDF4',
      border: '#BBF7D0',
      phases: [
        { phase: 'Phase 1', title: 'Technical Training', items: ['Core & Advanced Java', 'Spring Boot Basics', 'Database Fundamentals (MySQL)', 'HTML, CSS & JavaScript'] },
        { phase: 'Phase 2', title: 'Mini Project', items: ['Requirement Analysis', 'Module-wise Development', 'Unit Testing', 'Code Review'] },
        { phase: 'Phase 3', title: 'Internship Project', items: ['Real-Time Project Assignment', 'Team Collaboration', 'Project Demo', 'Internship Completion Certificate'] },
      ],
      certificates: ['Internship Certificate', 'Project Completion Certificate'],
    },
    {
      duration: '6 Months Internship',
      color: '#7C3AED',
      bg: '#FAF5FF',
      border: '#DDD6FE',
      phases: [
        { phase: 'Phase 1', title: 'Technical Training', items: ['Core & Advanced Java', 'Spring Boot & Hibernate', 'REST API Development', 'Frontend (HTML, CSS, JS, Bootstrap)'] },
        { phase: 'Phase 2', title: 'Capstone Project', items: ['Full Stack Application Build', 'Database Design & Integration', 'API Testing with Postman', 'Version Control with Git & GitHub'] },
        { phase: 'Phase 3', title: 'Live Project Internship', items: ['Industry Live Project', 'Agile/Scrum Workflow', 'Client-Style Project Delivery', 'Mock Interviews & Resume Building'] },
      ],
      certificates: ['Internship Certificate', 'Capstone Project Certificate', 'Live Project Experience Certificate'],
    },
  ],

  learnColumns: [
    { title: 'Backend (Java)', color: '#F97316', items: ['Core Java & OOP Concepts', 'Collections & Exception Handling', 'Spring Boot Framework', 'Hibernate & JPA', 'REST API Development'] },
    { title: 'Database',       color: '#00758F', items: ['MySQL Fundamentals', 'Database Design & Normalization', 'SQL Queries & Joins', 'Stored Procedures', 'JDBC & ORM Integration'] },
    { title: 'Frontend',       color: '#2965F1', items: ['HTML5 & CSS3', 'JavaScript ES6', 'Bootstrap Framework', 'Responsive Web Design', 'DOM Manipulation'] },
    { title: 'Tools',          color: '#181717', items: ['Git & GitHub', 'Postman API Testing', 'Maven Build Tool', 'IDE (Eclipse / IntelliJ)', 'Agile & Scrum Basics'] },
  ],

  liveProjects: {
    checklist: [
      'Work on real client-style requirements',
      'Follow Agile/Scrum development process',
      'Collaborate in cross-functional teams',
      'Hands-on with Git version control & code reviews',
      'Deploy and demo a production-style application',
    ],
    projects: ['Student Management System', 'CRM Application', 'Hospital Management System', 'LMS Portal', 'E-Commerce Platform', 'HRMS Application'],
    moreLabel: '...and many more',
  },
}

/* ════════════════════════════════════════════════════════════
   COMPONENT
   ════════════════════════════════════════════════════════════ */
const CourseProgramOverview = ({ data = JAVA_FULLSTACK_OVERVIEW }) => {
  const { technologies, modes, internships, learnColumns, liveProjects } = data

  return (
    <section id="cpo-section" style={{ padding: '64px 5%' }}>
      <style>{`
        .cpo-tech-icon { width:48px; height:48px; border-radius:12px; display:flex; align-items:center; justify-content:center; color:#fff; font-size:13px; font-weight:800; box-shadow:0 4px 12px rgba(15,23,42,0.12); }
        .cpo-tech-card { display:flex; flex-direction:column; align-items:center; gap:10px; width:104px; }
        .cpo-tech-name { font-size:12.5px; font-weight:700; color:#1E293B; text-align:center; line-height:1.3; }
        .cpo-mode-card { flex:1 1 220px; background:#fff; border:1.5px solid #E2E8F0; border-radius:14px; padding:24px; box-shadow:0 2px 8px rgba(0,0,0,0.04); }
        .cpo-phase-col { flex:1 1 200px; background:rgba(255,255,255,0.6); border-radius:12px; padding:16px 18px; }
        .cpo-proj-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(220px,1fr)); gap:12px; }
        @media(max-width:768px) { .cpo-tech-card { width:84px; } .cpo-tech-icon { width:42px; height:42px; font-size:11.5px; } }
      `}</style>

      <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 56 }}>

        {/* ── TECHNOLOGIES YOU WILL LEARN ── */}
        <div>
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.12em', color: '#2563EB', textTransform: 'uppercase', marginBottom: 10 }}>Tech Stack</div>
            <h2 style={{ fontSize: 28, fontWeight: 900, color: '#0F172A', margin: 0, letterSpacing: '-0.02em' }}>Technologies You Will Learn</h2>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 20 }}>
            {technologies.map(t => (
              <div key={t.name} className="cpo-tech-card">
                <TechIcon label={t.label} color={t.color} />
                <span className="cpo-tech-name">{t.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── PROGRAM MODES ── */}
        <div>
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.12em', color: '#2563EB', textTransform: 'uppercase', marginBottom: 10 }}>Flexible Learning</div>
            <h2 style={{ fontSize: 28, fontWeight: 900, color: '#0F172A', margin: 0, letterSpacing: '-0.02em' }}>Program Modes</h2>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
            {modes.map(m => (
              <div key={m.title} className="cpo-mode-card">
                <div style={{ width: 52, height: 52, borderRadius: 12, background: `${m.color}1A`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                  <ModeIcon path={m.icon} color={m.color} />
                </div>
                <div style={{ fontSize: 17, fontWeight: 800, color: '#0F172A', marginBottom: 8 }}>{m.title}</div>
                <div style={{ fontSize: 13.5, color: '#64748B', lineHeight: 1.6 }}>{m.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── INTERNSHIP BREAKDOWNS ── */}
        {internships.map(intern => (
          <div key={intern.duration}>
            <div style={{ textAlign: 'center', marginBottom: 32 }}>
              <h2 style={{ fontSize: 26, fontWeight: 900, color: '#0F172A', margin: 0, letterSpacing: '-0.02em' }}>{intern.duration}</h2>
            </div>
            <div style={{ background: intern.bg, border: `1.5px solid ${intern.border}`, borderRadius: 16, padding: 24 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 20 }}>
                {intern.phases.map(p => (
                  <div key={p.phase} className="cpo-phase-col">
                    <div style={{ fontSize: 10.5, fontWeight: 800, color: intern.color, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 4 }}>{p.phase}</div>
                    <div style={{ fontSize: 15.5, fontWeight: 800, color: '#0F172A', marginBottom: 12 }}>{p.title}</div>
                    {p.items.map(item => (
                      <div key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: '#374151', marginBottom: 8 }}>
                        <Check color={intern.color} />
                        {item}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center', borderTop: `1px solid ${intern.border}`, paddingTop: 18 }}>
                {intern.certificates.map(c => (
                  <div key={c} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: `1px solid ${intern.border}`, borderRadius: 50, padding: '8px 16px', fontSize: 12.5, fontWeight: 700, color: intern.color }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={intern.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="6" /><path d="M9 13.5L7 22l5-3 5 3-2-8.5" /></svg>
                    {c}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}

        {/* ── WHAT YOU WILL LEARN ── */}
        <div>
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.12em', color: '#2563EB', textTransform: 'uppercase', marginBottom: 10 }}>Curriculum Breakdown</div>
            <h2 style={{ fontSize: 28, fontWeight: 900, color: '#0F172A', margin: 0, letterSpacing: '-0.02em' }}>What You Will Learn</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(230px,1fr))', gap: 16 }}>
            {learnColumns.map(col => (
              <div key={col.title} style={{ background: '#fff', border: '1.5px solid #E2E8F0', borderRadius: 14, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <div style={{ background: col.color, padding: '14px 18px' }}>
                  <div style={{ fontSize: 14.5, fontWeight: 800, color: '#fff' }}>{col.title}</div>
                </div>
                <div style={{ padding: '16px 18px' }}>
                  {col.items.map(item => (
                    <div key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: '#374151', marginBottom: 10 }}>
                      <Check color={col.color} />
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── LIVE PROJECT INTERNSHIP EXPERIENCE ── */}
        <div style={{ background: '#FEF2F2', border: '1.5px solid #FECACA', borderRadius: 16, padding: 28 }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <h2 style={{ fontSize: 26, fontWeight: 900, color: '#0F172A', margin: 0, letterSpacing: '-0.02em' }}>Live Project Internship Experience</h2>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24, maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            {liveProjects.checklist.map(item => (
              <div key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13.5, color: '#374151' }}>
                <Check color="#DC2626" />
                {item}
              </div>
            ))}
          </div>
          <div className="cpo-proj-grid">
            {liveProjects.projects.map(p => (
              <div key={p} style={{ background: '#fff', border: '1px solid #FECACA', borderRadius: 10, padding: '14px 18px', fontSize: 13.5, fontWeight: 700, color: '#0F172A', textAlign: 'center' }}>
                {p}
              </div>
            ))}
            <div style={{ background: 'transparent', border: '1px dashed #FCA5A5', borderRadius: 10, padding: '14px 18px', fontSize: 13.5, fontWeight: 700, color: '#DC2626', textAlign: 'center' }}>
              {liveProjects.moreLabel}
            </div>
          </div>
        </div>

      </div>
    </section>
  )
}

export default CourseProgramOverview
